import { BehaviorSubject, Subscription, filter, Observable, of, take, timeout, catchError } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { User } from './shared/interfaces/user.interface';
import { UserService } from './services/user.service';
import { webddn } from 'src/environments/webddn';
import {
  IRbacBinding,
  IRbacPermission,
  ScopedRbacService
} from './admin/scoped-rbac/services/scoped-rbac.service';

import { normalizeClientRole, roleAllows } from './utils/normalize-role';
import { parseDdnSession } from './utils/ddn-session';
import {
  ACTION_BINDING_FALLBACKS,
  FEATURE_BINDING_FALLBACKS,
  NAMED_PERMISSION_FALLBACKS,
  ROUTE_BINDING_FALLBACKS,
} from 'src/environments/scoped-rbac-fallbacks';
import { content } from 'src/environments/data';

@Injectable({
  providedIn: 'root'
})
export class AclService implements OnDestroy {
  private userRoles: string[] = [];
  private grantedPermissionKeys = new Set<string>();
  private permissionByKey = new Map<string, IRbacPermission>();
  private bindingsByKindTarget = new Map<string, IRbacBinding>();
  /** Documents with at least one resource grant: `kind::id` */
  private resourceLocked = new Set<string>();
  /** Session grants: `kind::id::permission_key` */
  private resourceGrantKeys = new Set<string>();
  private user: User | undefined;
  private effectiveLoaded = false;
  /** Monotonic sequence so only the latest effective-ACL load may apply its result. */
  private effectiveLoadSeq = 0;
  /** app_type whose effective-ACL load is in flight (coalesces concurrent triggers). */
  private effectiveInFlight: string | null = null;
  /** Distinct char-string perms already warned in non-prod. */
  private charAclWarned = new Set<string>();
  private effectiveReadySubject = new BehaviorSubject<boolean>(false);

  private permissionsChangedSubject = new BehaviorSubject<boolean>(false);
  public permissionsChanged$ = this.permissionsChangedSubject.asObservable();

  private userSubscription: Subscription | undefined;
  private rbacBroadcast: BroadcastChannel | null = null;

  constructor(
    private cookieService: CookieService,
    private userService: UserService,
    private scopedRbacService: ScopedRbacService
  ) {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      // Defer ACL initialization on mobile to reduce unused JS / block time during load
      setTimeout(() => {
        this.initializeFromStaticData();
        this.subscribeToUserChanges();
        this.subscribeCrossTabRbac();
      }, 5000);
    } else {
      this.initializeFromStaticData();
      this.subscribeToUserChanges();
      this.subscribeCrossTabRbac();
    }
  }

  private subscribeCrossTabRbac(): void {
    try {
      if (typeof BroadcastChannel === 'undefined') {
        return;
      }
      this.rbacBroadcast = new BroadcastChannel('webddn-rbac');
      this.rbacBroadcast.onmessage = (ev) => {
        if (ev?.data?.type === 'reload-effective') {
          this.reloadEffective();
        }
      };
    } catch {
      this.rbacBroadcast = null;
    }
  }

  /** Notify other tabs to reload effective RBAC. */
  broadcastRbacReload(): void {
    try {
      this.rbacBroadcast?.postMessage({ type: 'reload-effective' });
    } catch {
      /* ignore */
    }
  }

  private subscribeToUserChanges(): void {
    this.userSubscription = this.userService.user$.subscribe((user) => {
      this.user = user || undefined;
      this.updateUserRoles();
      if (this.user?.role) {
        this.effectiveReadySubject.next(false);
        this.loadEffectiveScopedRbac();
      } else {
        this.clearEffectiveScopedRbac();
        this.initializeFromStaticData();
        this.effectiveReadySubject.next(true);
        this.notifyPermissionsChanged();
      }
    });
  }

  private updateUserRoles(): void {
    const nextRoles = this.user?.role
      ? ([normalizeClientRole(this.user.role)].filter(Boolean) as string[])
      : [];

    // `syncUserFromSession()` calls this on every `hasPermission()` check, so a
    // single render pass (header/nav/menus) hits it dozens of times. Bail out
    // when roles are unchanged to avoid reallocating the array and spamming the
    // console — only do work (and log) on an actual role change.
    const unchanged =
      nextRoles.length === this.userRoles.length &&
      nextRoles.every((role, i) => role === this.userRoles[i]);
    if (unchanged) {
      return;
    }

    this.userRoles = nextRoles;
    if (!environment.production) {
      console.log('AclService: Updated user roles:', this.userRoles);
    }
  }

  private clearEffectiveScopedRbac(): void {
    this.grantedPermissionKeys.clear();
    this.permissionByKey.clear();
    this.bindingsByKindTarget.clear();
    this.resourceLocked.clear();
    this.resourceGrantKeys.clear();
    this.effectiveLoaded = false;
  }

  /** Offline binding bootstrap from generated defaults (overlaid by effective load). */
  private seedOfflineBindings(): void {
    const routeBindings = {
      ...ROUTE_BINDING_FALLBACKS,
      ['/' + content.WEBDDN_PASSENGER]: 'feature.passenger'
    };

    for (const [target, permission_key] of Object.entries(routeBindings)) {
      this.bindingsByKindTarget.set(`route::${target}`, {
        app_type: '*',
        kind: 'route',
        target,
        permission_key,
      });
    }
    for (const [target, permission_key] of Object.entries(ACTION_BINDING_FALLBACKS)) {
      this.bindingsByKindTarget.set(`action::${target}`, {
        app_type: '*',
        kind: 'action',
        target,
        permission_key,
      });
    }
    for (const [target, permission_key] of Object.entries(FEATURE_BINDING_FALLBACKS)) {
      this.bindingsByKindTarget.set(`feature::${target}`, {
        app_type: '*',
        kind: 'feature',
        target,
        permission_key,
      });
    }
  }

  private initializeFromStaticData(): void {
    this.seedOfflineBindings();
    this.notifyPermissionsChanged();
  }

  /**
   * Load once per session/role — keep hot path in-memory.
   *
   * Stale-write isolation for the multi-tenant shared scope: this runs on every
   * `user$` emission (login / role change / refreshPermissions), so concurrent
   * loads must not clobber each other.
   * - Coalesce: a single in-flight request per app_type is enough; extra triggers
   *   skip the redundant HTTP call.
   * - Sequence guard: tag each load with a monotonic sequence and apply its
   *   response only if it is still the latest, so a slower, older request can
   *   never overwrite fresher effective ACL state.
   */
  private loadEffectiveScopedRbac(): void {
    const appType = webddn.APP_TYPE || '*';
    const subtype = (webddn as { APP_SUBTYPE?: string }).APP_SUBTYPE || '*';
    const scopeKey = `${appType}::${subtype}`;
    // Coalesce: a load for this scope is already in flight; the latest response
    // wins via the sequence guard, so don't fire a redundant request.
    if (this.effectiveInFlight === scopeKey) {
      return;
    }
    const seq = ++this.effectiveLoadSeq;
    this.effectiveInFlight = scopeKey;
    this.scopedRbacService.getEffective(appType, subtype).subscribe({
      next: (data) => {
        // A newer load superseded this one — drop the stale response.
        if (seq !== this.effectiveLoadSeq) {
          return;
        }
        this.effectiveInFlight = null;
        this.clearEffectiveScopedRbac();
        // Live success: apply Mongo effective only (do not resurrect offline fallbacks).
        const role = normalizeClientRole(this.userRoles[0]);
        for (const p of data.permissions || []) {
          this.permissionByKey.set(p.key, p);
          if (role && roleAllows(p.roles_allow, role)) {
            this.grantedPermissionKeys.add(p.key);
          }
        }
        for (const b of data.bindings || []) {
          this.bindingsByKindTarget.set(`${b.kind}::${b.target}`, b);
        }
        for (const key of data.resource_locked || []) {
          this.resourceLocked.add(key);
        }
        for (const g of data.resource_grants || []) {
          this.resourceGrantKeys.add(
            `${g.resource_kind}::${g.resource_id}::${g.permission_key}`
          );
        }
        this.effectiveLoaded = true;
        this.effectiveReadySubject.next(true);
        this.notifyPermissionsChanged();
      },
      error: () => {
        if (seq !== this.effectiveLoadSeq) {
          return;
        }
        this.effectiveInFlight = null;
        // Effective load failed — use offline fallbacks only.
        this.clearEffectiveScopedRbac();
        this.seedOfflineBindings();
        this.effectiveLoaded = false;
        this.effectiveReadySubject.next(true);
        this.notifyPermissionsChanged();
      }
    });
  }

  private notifyPermissionsChanged(): void {
    if (!environment.production) {
      console.log('AclService: Notifying permissions changed');
    }
    this.permissionsChangedSubject.next(true);
  }

  setRoles(roles: string[]) {
    if (!environment.production) {
      console.log('AclService: setRoles called with:', roles);
    }
    this.userRoles = roles;
    this.notifyPermissionsChanged();
  }

  hasRole(role: string): boolean {
    return this.userRoles.includes(role);
  }

  /**
   * Named keys (contain `.`) use granted permission set / roles_allow.
   * `action:target` / `feature:target` resolve via Bindings (with offline fallbacks).
   * Legacy char-strings (e.g. `sa`) use substring match on user role.
   */
  hasPermission(permission: any): boolean {
    if (!permission) {
      return false;
    }

    this.syncUserFromSession();

    if (!this.user?.token) {
      return false;
    }

    const perm = String(permission);

    if (perm.startsWith('action:')) {
      return this.hasAction(perm.slice('action:'.length));
    }
    if (perm.startsWith('feature:')) {
      return this.hasFeature(perm.slice('feature:'.length));
    }
    // resource:kind:id:permission_key  (id may contain ':' — permission is last segment with '.')
    if (perm.startsWith('resource:')) {
      return this.hasResourceFromAclString(perm.slice('resource:'.length));
    }

    if (perm.includes('.')) {
      if (
        (perm === 'action.team.sport_manage' ||
          perm === 'action.journey.sport_create' ||
          perm === 'sport.tournament') &&
        webddn.APP_TYPE !== 'sport'
      ) {
        return false;
      }
      if (this.grantedPermissionKeys.has(perm)) {
        return true;
      }
      const meta = this.permissionByKey.get(perm);
      if (meta?.roles_allow) {
        return this.userRoles.some((role) => roleAllows(meta.roles_allow, role));
      }
      const fallbackAllow = NAMED_PERMISSION_FALLBACKS[perm];
      if (fallbackAllow) {
        return this.userRoles.some((role) => roleAllows(fallbackAllow, role));
      }
      return false;
    }

    if (!environment.production && !this.charAclWarned.has(perm)) {
      this.charAclWarned.add(perm);
      console.warn(
        `AclService: deprecated char ACL "${perm}" — migrate to named permission / action: / feature:`
      );
    }
    // Production: deny bare char-string ACLs (named keys / action: / feature: only).
    if (environment.production) {
      return false;
    }
    return this.userRoles.some((role) => perm.includes(role));
  }

  /** Keep ACL state aligned with ddnsession + UserService (raw token cookies lack role). */
  private syncUserFromSession(): void {
    const parsed = parseDdnSession(this.cookieService.get('ddnsession'));
    const fromService = this.userService.getCurrentUser();

    if (fromService?.token) {
      this.user = {
        ...parsed,
        ...fromService,
        token: fromService.token || parsed?.token,
        role: fromService.role || parsed?.role,
      };
      this.updateUserRoles();
      return;
    }

    if (parsed?.token) {
      this.user = parsed;
      this.updateUserRoles();
      return;
    }

    if (this.user) {
      this.user = undefined;
      this.updateUserRoles();
      this.clearEffectiveScopedRbac();
      this.notifyPermissionsChanged();
    }
  }

  /** Binding check: route | action | feature target → permission. */
  hasBinding(kind: 'route' | 'action' | 'feature', target: string): boolean {
    const binding = this.bindingsByKindTarget.get(`${kind}::${target}`);
    if (!binding) {
      return false;
    }
    return this.hasPermission(binding.permission_key);
  }

  /** Longest-prefix match among route bindings for normalized path. */
  private resolveRouteBinding(path: string): IRbacBinding | undefined {
    const normalized = this.normalizeRoutePath(path);
    // Exact path match only (child paths need their own bindings).
    const exact = this.bindingsByKindTarget.get(`route::${normalized}`);
    if (exact) {
      return exact;
    }
    return undefined;
  }

  private normalizeRoutePath(url: string): string {
    const path = (url || '').split('?')[0].split('#')[0];
    if (!path) {
      return '/';
    }
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }

  hasRouteBinding(path: string): boolean {
    return !!this.resolveRouteBinding(path);
  }

  /** True when a route Binding exists and its permission is granted. */
  hasRouteAccess(path: string): boolean {
    const binding = this.resolveRouteBinding(path);
    if (!binding) {
      return false;
    }
    return this.hasPermission(binding.permission_key);
  }

  hasFeature(featureKey: string): boolean {
    if (this.bindingsByKindTarget.has(`feature::${featureKey}`)) {
      return this.hasBinding('feature', featureKey);
    }
    const fallback = FEATURE_BINDING_FALLBACKS[featureKey];
    if (fallback) {
      return this.hasPermission(fallback);
    }
    // No binding: treat as permission key when it looks like one
    if (featureKey.includes('.')) {
      return this.hasPermission(featureKey);
    }
    return false;
  }

  /** Action Binding → permission; offline fallback via generated ACTION_BINDING_FALLBACKS. */
  hasAction(actionTarget: string): boolean {
    if (
      (actionTarget === 'team.sport_manage' || actionTarget === 'journey.sport_create') &&
      webddn.APP_TYPE !== 'sport'
    ) {
      return false;
    }
    if (this.bindingsByKindTarget.has(`action::${actionTarget}`)) {
      return this.hasBinding('action', actionTarget);
    }
    const fallbackPerm = ACTION_BINDING_FALLBACKS[actionTarget];
    if (fallbackPerm) {
      return this.hasPermission(fallbackPerm);
    }
    return false;
  }

  /**
   * Convenience for delete/edit buttons: action Binding + optional per-document lock.
   * `permissionKey` should be the named key stored on resource grants (e.g. feature.team.manage).
   */
  canMutateResource(
    resourceKind: string,
    resourceId: string,
    actionTarget: string,
    permissionKey: string
  ): boolean {
    if (!this.hasAction(actionTarget)) {
      return false;
    }
    return this.hasResourceAccess(resourceKind, String(resourceId || ''), permissionKey);
  }

  /**
   * Per-document access.
   * Requires named permission (or action:/feature: that resolves to one).
   * If the document has any resource grants (`resource_locked`), also requires a matching grant
   * for this session (unless role is Super Admin `s`).
   * If unlocked, permission alone is enough (compat with company_id / assignee filters).
   */
  hasResourceAccess(resourceKind: string, resourceId: string, permissionKey: string): boolean {
    if (!resourceKind || !resourceId || !permissionKey) {
      return false;
    }
    const namedKey = this.resolveNamedPermissionKey(permissionKey);
    if (!this.hasPermission(permissionKey) && !this.hasPermission(namedKey)) {
      return false;
    }
    if (this.userRoles.includes('s')) {
      return true;
    }
    const docKey = `${resourceKind}::${resourceId}`;
    if (!this.resourceLocked.has(docKey)) {
      return true;
    }
    return (
      this.resourceGrantKeys.has(`${docKey}::${namedKey}`) ||
      this.resourceGrantKeys.has(`${docKey}::${permissionKey}`)
    );
  }

  /** Map action:/feature: / named key → stored grant permission_key. */
  private resolveNamedPermissionKey(permissionKey: string): string {
    if (permissionKey.startsWith('action:')) {
      const target = permissionKey.slice('action:'.length);
      const binding = this.bindingsByKindTarget.get(`action::${target}`);
      if (binding?.permission_key) {
        return binding.permission_key;
      }
      return ACTION_BINDING_FALLBACKS[target] || permissionKey;
    }
    if (permissionKey.startsWith('feature:')) {
      const target = permissionKey.slice('feature:'.length);
      const binding = this.bindingsByKindTarget.get(`feature::${target}`);
      if (binding?.permission_key) {
        return binding.permission_key;
      }
      return FEATURE_BINDING_FALLBACKS[target] || permissionKey;
    }
    return permissionKey;
  }

  /** Parse `kind:id:permission.key` (permission is the last dotted segment group). */
  private hasResourceFromAclString(rest: string): boolean {
    const lastColon = rest.lastIndexOf(':');
    if (lastColon <= 0) {
      return false;
    }
    const permissionKey = rest.slice(lastColon + 1);
    const beforePerm = rest.slice(0, lastColon);
    const firstColon = beforePerm.indexOf(':');
    if (firstColon <= 0) {
      return false;
    }
    const resourceKind = beforePerm.slice(0, firstColon);
    const resourceId = beforePerm.slice(firstColon + 1);
    return this.hasResourceAccess(resourceKind, resourceId, permissionKey);
  }

  getCurrentUser(): User | undefined {
    return this.user;
  }

  refreshPermissions(): void {
    if (this.user?.role) {
      this.effectiveReadySubject.next(false);
      this.loadEffectiveScopedRbac();
    } else {
      this.notifyPermissionsChanged();
    }
  }

  /**
   * Public reload of effective RBAC (after Matrix save, Push, impersonation, cross-tab).
   */
  reloadEffective(): void {
    this.effectiveReadySubject.next(false);
    this.effectiveInFlight = null;
    this.loadEffectiveScopedRbac();
  }

  isEffectiveLoaded(): boolean {
    return this.effectiveLoaded;
  }

  /**
   * Wait until effective RBAC load finishes (success or fallback).
   * Used by aclGuard so Mongo roles_allow win over a race with offline maps.
   */
  whenEffectiveReady(): Observable<boolean> {
    if (!this.user?.role) {
      return of(true);
    }
    if (this.effectiveLoaded) {
      return of(true);
    }
    if (this.effectiveInFlight === null) {
      this.loadEffectiveScopedRbac();
    }
    return this.effectiveReadySubject.pipe(
      filter((ready) => ready === true),
      take(1),
      timeout({ first: 8000 }),
      catchError(() => of(true))
    );
  }

  getCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=').map((c) => c.trim());
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    try {
      this.rbacBroadcast?.close();
    } catch {
      /* ignore */
    }
    this.rbacBroadcast = null;
    this.permissionsChangedSubject.complete();
    this.effectiveReadySubject.complete();
  }
}
